import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../../api";
import { logoUrl } from "../../branding";
import { formatDateTime } from "../../../shared/time";
import type { RegistrationEvent } from "../../../shared/types";
import { ErrorNote } from "../../ui";
import { CapacityMeter, PageFrame, messageOf } from "../calendar/chrome";
import type { SignupResult } from "./types";

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function PublicSignupPage() {
  const { slug = "" } = useParams();
  const [registration, setRegistration] = useState<RegistrationEvent | null>(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [party, setParty] = useState("1");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<SignupResult | null>(null);

  useEffect(() => {
    let live = true;
    setError("");
    setResult(null);
    void api<RegistrationEvent>(`/api/v1/public/registrations/${encodeURIComponent(slug)}`)
      .then((row) => { if (live) setRegistration(row); })
      .catch((caught: unknown) => { if (live) { setRegistration(null); setError(messageOf(caught)); } });
    return () => { live = false; };
  }, [slug]);

  const size = Number(party);
  const sizeOk = Number.isInteger(size) && size >= 1 && size <= 12;
  const overflows = registration != null && sizeOk && size > registration.remaining;
  const blocked = Boolean(registration && overflows && !registration.allowWaitlist);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!registration) return;
    setError("");
    if (!name.trim()) {
      setError("Enter the name for this signup.");
      return;
    }
    if (!EMAIL.test(email.trim().toLowerCase())) {
      setError("Enter an email address.");
      return;
    }
    if (!sizeOk) {
      setError("Party size is a whole number from 1 to 12.");
      return;
    }
    if (blocked) {
      setError(`Only ${registration.remaining} places remain, and this signup does not keep a waitlist.`);
      return;
    }
    setSaving(true);
    try {
      const created = await api<SignupResult>(`/api/v1/public/registrations/${encodeURIComponent(slug)}/signups`, {
        method: "POST",
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase(), partySize: size }),
      });
      setResult(created);
      setRegistration(await api<RegistrationEvent>(`/api/v1/public/registrations/${encodeURIComponent(slug)}`));
      setName("");
      setEmail("");
      setParty("1");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageFrame className="public-sheet" kicker="MB Live signup" title={registration?.title ?? "Signup"} lede="This page only records a local signup. It does not take payment or send email.">
      <Link className="public-brand" to="/"><img src={logoUrl} alt="" /><span>MB Live</span></Link>
      <p><Link to="/">Back to MB Live</Link></p>
      {error && <ErrorNote>{error}</ErrorNote>}
      {!registration && !error ? <p>Loading the signup…</p> : null}
      {registration && (
        <div className="stack">
          <article className="panel panel-pad stack">
            <p>{formatDateTime(registration.startsAt)} – {formatDateTime(registration.endsAt)}</p>
            <p className="muted">{registration.place || "Place to be confirmed"}</p>
            <p className="prose">{registration.description}</p>
            <CapacityMeter confirmed={registration.confirmedCount} remaining={registration.remaining} capacity={registration.capacity} />
            {registration.allowWaitlist ? <p className="faint">A party larger than the places left goes on the waitlist instead of taking seats.</p> : <p className="faint">There is no waitlist. A party has to fit in the places left.</p>}
          </article>
          {result && (
            <article className="ok-note stack" role="status">
              <strong>{result.status === "waitlist" ? "You are on the waitlist." : "You are confirmed."}</strong>
              <p>{result.confirmationNote || "The desk has the signup. No email receipt is sent."}</p>
              <p>{result.remaining === 1 ? "1 place left." : `${result.remaining} places left.`}</p>
              <label className="field">Signup reference<input readOnly value={result.id} /></label>
              <p>This page cannot cancel a signup. Ask a coordinator to release the place from the registration desk — that returns confirmed seats to the count.</p>
              <button className="btn" type="button" onClick={() => setResult(null)}>Add another person</button>
            </article>
          )}
          <form className="panel panel-pad form-grid" onSubmit={(event) => void submit(event)}>
            <div className="field"><label htmlFor="signup-name">Name</label><input id="signup-name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" maxLength={140} required /></div>
            <div className="field"><label htmlFor="signup-email">Email</label><input id="signup-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required /></div>
            <div className="field"><label htmlFor="signup-party">Party size</label><input id="signup-party" inputMode="numeric" value={party} onChange={(event) => setParty(event.target.value)} required /></div>
            {overflows && registration.allowWaitlist ? <p className="notice">This party is larger than the places left, so the signup will waitlist.</p> : null}
            {blocked ? <p className="notice">Only {registration.remaining} places remain. Reduce the party or wait for a cancellation.</p> : null}
            <button className="btn primary" type="submit" disabled={saving || blocked}>{saving ? "Sending…" : "Sign up"}</button>
          </form>
        </div>
      )}
    </PageFrame>
  );
}
